import { zodResolver } from '@hookform/resolvers/zod';
import {
  paymentGatewayConfigControllerActivate,
  paymentGatewayConfigControllerCreate,
  paymentGatewayConfigControllerDelete,
  paymentGatewayConfigControllerList,
  paymentGatewayConfigControllerUpdate,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Pencil, ShieldCheck, Trash2, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AppLayout } from '../../../components/layout/app-layout';
import { PageHeader } from '../../../components/layout/page-header';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Skeleton } from '../../../components/ui/skeleton';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/config/payment-gateway/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('system:payment-config')) throw redirect({ to: '/dashboard' });
  },
  component: PaymentGatewayPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface GatewayConfig {
  id: string;
  provider: 'TAHSEEL' | 'MOCK';
  isActive: boolean;
  label: string;
  merchantId: string;
  securityKey: string;
  entityCode: string;
  deptCode: string;
  branchId?: string | null;
  serviceCode?: string | null;
  paymentLinkExpiryHours: number;
  baseUrl: string;
  inquiryUrl: string;
  serviceUsername: string;
  servicePassword: string;
  updatedBy?: string | null;
  updatedAt: string;
}

// ── Form schema ───────────────────────────────────────────────────────────────

const gatewaySchema = z.object({
  label: z.string().min(1, 'Label is required'),
  provider: z.enum(['TAHSEEL', 'MOCK']),
  merchantId: z.string().min(1, 'Merchant ID is required'),
  securityKey: z.string().optional(),
  entityCode: z.string().min(1, 'Entity Code is required'),
  deptCode: z.string().min(1, 'Department Code is required'),
  branchId: z.string().optional(),
  serviceCode: z.string().optional(),
  paymentLinkExpiryHours: z.coerce.number().int().min(1),
  baseUrl: z.string().url('Must be a valid URL'),
  inquiryUrl: z.string().url('Must be a valid URL'),
  serviceUsername: z.string().min(1, 'Service Username is required'),
  servicePassword: z.string().optional(),
});

type GatewayFormData = z.infer<typeof gatewaySchema>;

// ── Page ──────────────────────────────────────────────────────────────────────

function PaymentGatewayPage() {
  const qc = useQueryClient();
  const [editTarget, setEditTarget] = useState<GatewayConfig | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [activateTarget, setActivateTarget] = useState<GatewayConfig | null>(null);

  const { data: configs = [], isLoading } = useQuery({
    queryKey: ['payment-gateway-configs'],
    queryFn: () => paymentGatewayConfigControllerList() as Promise<GatewayConfig[]>,
  });

  const createMutation = useMutation({
    mutationFn: (dto: GatewayFormData) =>
      paymentGatewayConfigControllerCreate({ requestBody: dto as never }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-gateway-configs'] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: GatewayFormData }) =>
      paymentGatewayConfigControllerUpdate({ id, requestBody: dto as never }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-gateway-configs'] });
      setEditTarget(null);
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => paymentGatewayConfigControllerActivate({ id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-gateway-configs'] });
      setActivateTarget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => paymentGatewayConfigControllerDelete({ id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-gateway-configs'] }),
  });

  return (
    <AppLayout>
      <PageHeader
        title="Payment Gateway Configuration"
        description="Manage Tahseel payment gateway credentials"
        actions={
          <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>
            Add Configuration
          </Button>
        }
      />

      <div className="p-6">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((n) => (
              <Skeleton key={n} className="h-20 w-full rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white shadow-theme-sm overflow-hidden">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {['Label', 'Provider', 'Status', 'Last Updated', 'Actions'].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {configs.map((cfg) => (
                  <tr key={cfg.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">{cfg.label}</td>
                    <td className="px-6 py-4">
                      <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
                        {cfg.provider}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {cfg.isActive ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                          <ShieldCheck size={12} /> Active
                        </span>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-xs">
                      {new Date(cfg.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setEditTarget(cfg)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        {!cfg.isActive && (
                          <>
                            <button
                              type="button"
                              onClick={() => setActivateTarget(cfg)}
                              className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 transition-colors"
                              title="Activate"
                            >
                              <Zap size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteMutation.mutate(cfg.id)}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {configs.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                      No payment gateway configurations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <GatewayFormDialog
        open={showForm || editTarget !== null}
        title={editTarget ? 'Edit Configuration' : 'New Configuration'}
        defaultValues={editTarget ?? undefined}
        isPending={createMutation.isPending || updateMutation.isPending}
        error={(createMutation.error || updateMutation.error) as Error | null}
        onClose={() => {
          setShowForm(false);
          setEditTarget(null);
        }}
        onSubmit={(data) => {
          if (editTarget) updateMutation.mutate({ id: editTarget.id, dto: data });
          else createMutation.mutate(data);
        }}
      />

      {/* Activate confirmation dialog */}
      <Dialog open={activateTarget !== null} onOpenChange={() => setActivateTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activate Gateway</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This will replace the current active gateway. All new payments will immediately use
            <span className="font-semibold"> {activateTarget?.label}</span>.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivateTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={activateMutation.isPending}
              onClick={() => activateTarget && activateMutation.mutate(activateTarget.id)}
            >
              {activateMutation.isPending ? 'Activating…' : 'Activate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

// ── Form dialog ───────────────────────────────────────────────────────────────

function GatewayFormDialog({
  open,
  title,
  defaultValues,
  isPending,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  defaultValues?: Partial<GatewayConfig>;
  isPending: boolean;
  error: Error | null;
  onClose: () => void;
  onSubmit: (data: GatewayFormData) => void;
}) {
  const isEdit = Boolean(defaultValues?.id);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GatewayFormData>({
    resolver: zodResolver(gatewaySchema),
    defaultValues: {
      label: defaultValues?.label ?? '',
      provider: defaultValues?.provider ?? 'TAHSEEL',
      merchantId: defaultValues?.merchantId ?? '',
      securityKey: '',
      entityCode: defaultValues?.entityCode ?? '',
      deptCode: defaultValues?.deptCode ?? '',
      branchId: defaultValues?.branchId ?? '',
      serviceCode: defaultValues?.serviceCode ?? '',
      paymentLinkExpiryHours: defaultValues?.paymentLinkExpiryHours ?? 72,
      baseUrl: defaultValues?.baseUrl ?? '',
      inquiryUrl: defaultValues?.inquiryUrl ?? '',
      serviceUsername: defaultValues?.serviceUsername ?? '',
      servicePassword: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        label: defaultValues?.label ?? '',
        provider: defaultValues?.provider ?? 'TAHSEEL',
        merchantId: defaultValues?.merchantId ?? '',
        securityKey: '',
        entityCode: defaultValues?.entityCode ?? '',
        deptCode: defaultValues?.deptCode ?? '',
        branchId: defaultValues?.branchId ?? '',
        serviceCode: defaultValues?.serviceCode ?? '',
        paymentLinkExpiryHours: defaultValues?.paymentLinkExpiryHours ?? 72,
        baseUrl: defaultValues?.baseUrl ?? '',
        inquiryUrl: defaultValues?.inquiryUrl ?? '',
        serviceUsername: defaultValues?.serviceUsername ?? '',
        servicePassword: '',
      });
    }
  }, [open, defaultValues, reset]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Field label="Label" error={errors.label?.message}>
            <input
              {...register('label')}
              className={inputCls(!!errors.label)}
              placeholder="e.g. Tahseel Staging"
            />
          </Field>
          <Field label="Provider" error={errors.provider?.message}>
            <select {...register('provider')} className={inputCls(!!errors.provider)}>
              <option value="TAHSEEL">TAHSEEL</option>
              <option value="MOCK">MOCK</option>
            </select>
          </Field>
          <Field label="Merchant ID" error={errors.merchantId?.message}>
            <input {...register('merchantId')} className={inputCls(!!errors.merchantId)} />
          </Field>
          <Field
            label={isEdit ? 'Security Key (leave blank to keep existing)' : 'Security Key'}
            error={errors.securityKey?.message}
          >
            <input
              type="password"
              {...register('securityKey')}
              className={inputCls(!!errors.securityKey)}
              autoComplete="new-password"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Entity Code" error={errors.entityCode?.message}>
              <input {...register('entityCode')} className={inputCls(!!errors.entityCode)} />
            </Field>
            <Field label="Department Code" error={errors.deptCode?.message}>
              <input {...register('deptCode')} className={inputCls(!!errors.deptCode)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Branch ID (TP_BranchId)" error={errors.branchId?.message}>
              <input
                {...register('branchId')}
                className={inputCls(!!errors.branchId)}
                placeholder="e.g. 4"
              />
            </Field>
            <Field label="Service Code (TP_ServiceInfo)" error={errors.serviceCode?.message}>
              <input
                {...register('serviceCode')}
                className={inputCls(!!errors.serviceCode)}
                placeholder="e.g. L172"
              />
            </Field>
          </div>
          <Field label="Payment Link Expiry (hours)" error={errors.paymentLinkExpiryHours?.message}>
            <input
              type="number"
              min={1}
              {...register('paymentLinkExpiryHours', { valueAsNumber: true })}
              className={inputCls(!!errors.paymentLinkExpiryHours)}
              placeholder="72"
            />
          </Field>
          <Field label="Payment URL" error={errors.baseUrl?.message}>
            <input
              {...register('baseUrl')}
              className={inputCls(!!errors.baseUrl)}
              placeholder="https://..."
            />
          </Field>
          <Field label="Inquiry SOAP URL" error={errors.inquiryUrl?.message}>
            <input
              {...register('inquiryUrl')}
              className={inputCls(!!errors.inquiryUrl)}
              placeholder="https://..."
            />
          </Field>
          <Field label="Service Username" error={errors.serviceUsername?.message}>
            <input
              {...register('serviceUsername')}
              className={inputCls(!!errors.serviceUsername)}
            />
          </Field>
          <Field
            label={isEdit ? 'Service Password (leave blank to keep existing)' : 'Service Password'}
            error={errors.servicePassword?.message}
          >
            <input
              type="password"
              {...register('servicePassword')}
              className={inputCls(!!errors.servicePassword)}
              autoComplete="new-password"
            />
          </Field>

          {error && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
              {error.message}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  error,
  children,
}: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function inputCls(hasError: boolean) {
  return cn(
    'w-full rounded-xl border px-3 py-2 text-sm outline-none transition-colors',
    hasError
      ? 'border-red-300 focus:border-red-400 focus:ring-1 focus:ring-red-400'
      : 'border-gray-200 focus:border-brand-500 focus:ring-1 focus:ring-brand-500',
  );
}
